import React from 'react';
import { Clock } from 'lucide-react';

// Full audit trail of every status change a lesson plan has been through —
// draft, submit, HOD/admin decisions, resets, taught — so a reviewer or
// admin can see the whole flow, not just the current badge. `history` is
// the `status_history` array Backend/app/services/lesson_plan_workflow.py
// appends to on every transition.
const STATUS_LABELS = {
    draft: 'Draft',
    pending_approval: 'Awaiting HOD Review',
    pending_admin: 'Awaiting Admin Review',
    changes_requested: 'Changes Requested',
    approved: 'Approved',
    rejected: 'Rejected',
    taught: 'Taught',
    published: 'Published',
    archived: 'Archived',
};

const DOT_COLORS = {
    draft: 'bg-yellow-400',
    pending_approval: 'bg-amber-400',
    pending_admin: 'bg-orange-400',
    changes_requested: 'bg-purple-400',
    approved: 'bg-green-500',
    rejected: 'bg-red-500',
    taught: 'bg-indigo-400',
    published: 'bg-teal-400',
    archived: 'bg-gray-400',
};

const ROLE_LABELS = {
    teacher: 'Teacher',
    hod: 'HOD',
    admin: 'Admin',
    system_edit: 'System (auto)',
};

const formatWhen = (value) => {
    if (!value) return '';
    try {
        return new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
        return String(value);
    }
};

const StatusHistoryTimeline = ({ history, emptyText = 'No activity yet.' }) => {
    if (!Array.isArray(history) || history.length === 0) {
        return <p className="text-sm text-gray-500 italic">{emptyText}</p>;
    }

    const entries = [...history].reverse(); // most recent first

    return (
        <ol className="space-y-3">
            {entries.map((entry, idx) => (
                <li key={idx} className="flex gap-3">
                    <div className="flex flex-col items-center pt-1">
                        <span className={`w-2.5 h-2.5 rounded-full ${DOT_COLORS[entry.to] || 'bg-gray-400'}`} />
                        {idx < entries.length - 1 && <span className="flex-1 w-px bg-gray-200 mt-1" />}
                    </div>
                    <div className="flex-1 pb-1">
                        <div className="flex flex-wrap items-center gap-1.5 text-sm">
                            {entry.from && (
                                <>
                                    <span className="text-gray-500">{STATUS_LABELS[entry.from] || entry.from}</span>
                                    <span className="text-gray-400">→</span>
                                </>
                            )}
                            <span className="font-semibold text-gray-900">{STATUS_LABELS[entry.to] || entry.to}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 flex-wrap">
                            <Clock className="w-3 h-3" />
                            {formatWhen(entry.at)}
                            {entry.actor_name && (
                                <span>
                                    {' '}— {entry.actor_name}
                                    {entry.role ? ` (${ROLE_LABELS[entry.role] || entry.role})` : ''}
                                </span>
                            )}
                        </p>
                        {entry.reason && (
                            <p className="text-xs text-gray-600 mt-1 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1.5 whitespace-pre-wrap">
                                {entry.reason}
                            </p>
                        )}
                    </div>
                </li>
            ))}
        </ol>
    );
};

export default StatusHistoryTimeline;
